import './styles.scss';
import React, {useState} from 'react';
import {CloseOutlined, LoadingOutlined, SyncOutlined, UploadOutlined, CheckCircleOutlined} from '@ant-design/icons';
import Button from "../../components/common/Button/Button";
import DataApi from "../../api/Data/Data.api";
import UploadContractExport from "../../components/specific/Upload.contractExport/Upload.contractExport";
import {useQuery} from "react-query";
import DefaultLoader from "../../components/common/DefaultLoader/DefaultLoader";
import {EStartupSequenceStatus, ESyncNames} from "../../api/Data/Data.types";

interface DataSyncButtonProps {
    status:EStartupSequenceStatus,
    name:string,
    onSync:()=>void,
    syncing:boolean,
}

function DataSyncButton({ status, name, onSync, syncing }:DataSyncButtonProps) {

    function getStatusDependents(){
        let text = `Sync ${name}`;
        let icon = <SyncOutlined/>
        let buttonColor = 'primary';
        let available = true;
        switch(status){
            case EStartupSequenceStatus.InProgress:
                text = `Syncing ${name}...`;
                icon = <LoadingOutlined spin/>
                buttonColor = 'orange';
                available = false;
                break;
            case EStartupSequenceStatus.Cancelled:
                text = `Syncing ${name} error`;
                icon = <CloseOutlined/>
                buttonColor = 'red'
                break;
            case EStartupSequenceStatus.Completed:
                text = `Synced ${name}`
                icon = <CheckCircleOutlined/>
                buttonColor = 'green';
                break;
        }
        return {text, icon, buttonColor, available}
    }

    const { text, icon, buttonColor, available } = getStatusDependents();

    return (
        <Button onClick={buttonColor === 'green' ? ()=>null : onSync} icon={icon} loading={syncing} color={buttonColor as any} iconPosition='left'
                enabled={available}>
            {text}
        </Button>
    )
}

function DataSyncContainer() {
    const {isLoading, data, refetch} = useQuery('startupSequences', DataApi.getStartupSequences);

    const [naicsSyncing, setNaicsSyncing] = useState(false);
    const [pscSyncing, setPscSyncing] = useState(false);
    const [userSyncing, setUserSyncing] = useState(false);
    const [uploadVisible, setUploadVisible] = useState(false);

    async function onNaicsSync() {
        setNaicsSyncing(true);
        await DataApi.syncNaicsCodes();
        await refetch();
        setNaicsSyncing(false);
    }

    async function onPscSync() {
        setPscSyncing(true);
        await DataApi.syncPscCodes();
        await refetch();
        setPscSyncing(false);
    }

    function onUpload() {

    }

    async function onUserSync() {
        setUserSyncing(true);
        await DataApi.syncUsers();
        await refetch();
        setUserSyncing(false);
    }

    if (isLoading || !data) {
        return (
            <div className='default-loader-full'>
                <DefaultLoader text='Loading sync parameters...'/>
            </div>
        )
    }

    function getSyncsByName() {
        let ret = {};
        data.forEach(sync => {
            ret[sync.name] = sync.status;
        })
        return ret;
    }

    let syncsByName = getSyncsByName();
    let naicsStatus = syncsByName[ESyncNames.SyncNaicsCodes];
    let pscStatus = syncsByName[ESyncNames.SyncPscCodes];
    let userStatus = syncsByName[ESyncNames.SyncUsers]

    return (
        <div className='data-sync-container'>
            <div className='data-portion'>
                <div className='data-portion-left'>
                    <div className='header'>
                        NAICS Codes Sync
                    </div>
                    <div className='description'>
                        Syncing NAICS codes will delete all current NAICS codes and re-sync them with a newly updated
                        csv. You should <b>not</b> do this during business hours.
                    </div>

                </div>
                <div className='data-portion-right'>
                   <DataSyncButton status={naicsStatus} name='NAICS Codes' onSync={onNaicsSync} syncing={naicsSyncing}/>
                </div>
            </div>
            <div className='data-portion'>
                <div className='data-portion-left'>
                    <div className='header'>
                        PSC Codes Sync
                    </div>
                    <div className='description'>
                        Syncing PSC codes will delete all current PSC codes and re-sync them with a newly updated csv.
                        You should <b>not</b> do this during business hours.
                    </div>
                </div>
                <div className='data-portion-right'>
                    <DataSyncButton status={pscStatus} name='PSC Codes' onSync={onPscSync} syncing={pscSyncing}/>
                </div>
            </div>
            <div className='data-portion'>
                <div className='data-portion-left'>
                    <div className='header'>
                        User Sync
                    </div>
                    <div className='description'>
                        Sync the users from the user export csv. This should only be a one time operation.
                    </div>
                </div>
                <div className='data-portion-right'>
                    <DataSyncButton status={userStatus} name='Users' onSync={onUserSync} syncing={userSyncing}/>
                </div>
            </div>
            <div className='data-portion'>
                <div className='data-portion-left'>
                    <div className='header'>
                        Upload a SAM Contract Export File
                    </div>
                    <div className='description'>
                        Uploading a file here will automatically sync the contents of the export file into the FAMR
                        database. Syncs occur every morning and the new data will be available by 8AM EST.
                    </div>
                </div>
                <div className='data-portion-right'>
                    <Button onClick={() => setUploadVisible(true)} icon={<UploadOutlined/>} iconPosition='left'>
                        Upload Contract Export
                    </Button>
                </div>
            </div>
            <UploadContractExport visible={uploadVisible} onUpload={onUpload} onClose={() => setUploadVisible(false)}/>
        </div>
    )
}

export default DataSyncContainer;